/* eslint-disable no-nested-ternary */
import './deliveryInformation.scss';

import { lazy, Suspense, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { useCartContext } from '@/resources/hooks/useCartContext';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { canChangeOrder, getOrderMinValue, maskMoney } from '@/services/global';
import { IAttendanceType } from '@/store/ducks/order/types';

import RenderPickupAt from '../RenderPickupAt';
import AppliedDiscountModal from './shippingDiscountModal/AppliedDiscountModal';

const DeliveryMethodsModal = lazy(() => import('./DeliveryMethodsModal'));

function DeliveryInformation() {
  const [showMethods, setShowMethods] = useState<boolean>(false);
  const [showDiscount, setShowDiscount] = useState<boolean>(false);

  const { order } = useWebContext();
  const { min_order_amount, webapp_out_of_delivery, setWebappOutOfDelivery } = useCartContext();
  const { shipment } = order || {};

  const cartTotal = order?.cart?.total || 0;

  const getDeliveryInfoByType = (type: string) => {
    const types = shipment?.attendance_types || {};

    if (!shipment?.attendance_types || !(type in types)) return null;

    return shipment?.attendance_types[type as keyof IAttendanceType];
  };

  const getDeliveryInfo = () => {
    const shipmentType = shipment?.delivery?.type || '';
    const shipmentInfo = getDeliveryInfoByType(shipmentType);

    return shipmentInfo;
  };

  const deliveryInfo = getDeliveryInfo();
  const value = order?.cart.delivery_tax ? maskMoney(order?.cart.delivery_tax, true) : null;
  const hasDeliveryDiscount = order?.shipment?.delivery_discount;
  const hasDeliveryTypeDiscount = hasDeliveryDiscount?.type_discount;

  const getDeliveryName = () => {
    const name = shipment?.full_name_carrier ?? shipment?.carrier;

    return name || 'Padrão';
  };

  const handleShowMethods = () => {
    const { isValid } = canChangeOrder({
      type: 'forma de entrega',
      action: 'alterado',
    });

    if (!isValid) return;

    setShowMethods(true);
  };

  const handleGetTypeOfDescount = () => {
    if (hasDeliveryDiscount && hasDeliveryTypeDiscount) {
      if (hasDeliveryTypeDiscount === 'free' && !value) {
        return 'frete grátis';
      }
      if (hasDeliveryTypeDiscount === 'price' && hasDeliveryDiscount.price_discount > 0) {
        return 'desconto';
      }
    }
    return null;
  };

  useEffect(() => {
    if (value && Number(value) > Number(min_order_amount)) {
      setWebappOutOfDelivery(false);
    }
  }, [value]);

  useEffect(() => {
    if (!deliveryInfo && !shipment?.refresh_carrier) {
      toast.warn('Endereço fora da área de cobertura', {
        position: 'bottom-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
    }
  }, [order?.shipment?.address?.id]);

  return (
    <>
      {deliveryInfo && !webapp_out_of_delivery && (
        <>
          <div className="delivery-information">
            <div className="method">
              <span className="title d-flex align-items-center">
                Frete {getDeliveryName()}
                {hasDeliveryDiscount && handleGetTypeOfDescount() && (
                  <>
                    <button
                      className="btn btn-sm badge text-white badge-success ml-1"
                      onClick={() => setShowDiscount(true)}
                    >
                      {handleGetTypeOfDescount()}
                      <FaInfoCircle className="ml-1" />
                    </button>

                    <AppliedDiscountModal
                      show={showDiscount}
                      setShow={setShowDiscount}
                      deliveryDiscount={hasDeliveryDiscount}
                    />
                  </>
                )}
              </span>
              <small className="subtitle action" onClick={() => handleShowMethods()}>
                Ver outras opções de entrega
              </small>
            </div>
            <div className="infos">
              <span className="title price">{value || 'R$ 0,00'}</span>
              <small className="subtitle">
                Entrega:
                {shipment?.pickup_at ? (
                  <RenderPickupAt
                    pickupAt={shipment.pickup_at}
                    showTime={shipment?.delivery?.type === 'google-maps'}
                  />
                ) : (
                  'A definir'
                )}
              </small>
            </div>
          </div>
        </>
      )}

      {webapp_out_of_delivery && (
        <>
          <div className="without-method">
            {cartTotal < getOrderMinValue(order, min_order_amount) && (
              <span className="warning-text text-center">
                O valor mínimo do pedido não foi atingido
              </span>
            )}
            {!min_order_amount && (
              <span className="warning-text text-center">Endereço fora da área de cobertura</span>
            )}
          </div>
          {cartTotal < getOrderMinValue(order, min_order_amount) && (
            <div className="method d-flex justify-content-center">
              <Button
                type="button"
                variant="link"
                className="subtitle action pl-0"
                onClick={() => handleShowMethods()}
              >
                Ver outras opções de entrega
              </Button>
            </div>
          )}
        </>
      )}

      <Suspense fallback={<div />}>
        <DeliveryMethodsModal show={showMethods} setShow={setShowMethods} />
      </Suspense>
    </>
  );
}

export default DeliveryInformation;
